import DataExchangeService from '../data-exchange.service';
import { Consent } from '../../dataTypes/type-consent';
import { ConsentsStatusService } from './consents-status.service';
import { DataService } from '../utilities/data.service';
import { GetConsentsService } from './get-consents.service';
import { UtilityService } from '../utility.service';
import { LanguageCode } from '../../Enums/LanguageCode.enums';
const { ConsentString } = require('consent-string');
const pjson = require('../../../../package.json');

/**
 * Service responsible for several consent interactions that saves the status.
 */
export class SaveConsentsService {
    private utilityService: UtilityService;

    constructor(utilityService: UtilityService) {
        this.utilityService = utilityService;
    }

    /**
     * Sets all status to false, except essential consents;
     * @param action 
     * @param updatedBy 
     */
    denyAllConsentsExceptEssential(action, updatedBy): void {
        DataService.consents.forEach(consent => {
            ConsentsStatusService.setUnsavedConsentStatus(consent, false);
        });
        const isUpdateAll = true;
        this.saveConsents(action, updatedBy, isUpdateAll);
    }

    /**
     * Sets all status to true.
     * @param action 
     * @param updatedBy 
     */
    acceptAllConsents(action, updatedBy): void {
        DataService.consents.forEach(consent => {
            ConsentsStatusService.setUnsavedConsentStatus(consent, true);
        });
        const isUpdateAll = true;
        this.saveConsents(action, updatedBy, isUpdateAll);
    }

    saveConsentsFromCDCS() { }

    /**
     * consentService.saveSettings
     * isSaveAll => initialSaving
     * D0. set action and updatedBy of consent
     * D1. if isSaveAll => save all consents else => getConsentsThatHaveChanged and save it
     * D2. reset script tag if status changed: setTypeOfScriptTagIfTheyHaveChangedInTheUpdatedCategories
     * D3. update dataLayer: triggerCategoryDatLayerEvent & pushIntoDataExchangeIfTheyHaveChangedInTheUpdatedCategories
     * D4. save history prepareHistoryForChangedConsents => saveHistoryForConsents
     * D5. updateIABString
     * D6. update CDCS => updateIframeStorageToLatest
     * D7. triggered all custom functions => storeConsentStatusToLocalStorage, storeEventToLocalStorage
     * D8. save to localstorage => saveConsentsToLocalStorage
     * D9. save To backend => saveConsentsToServer
     * D10. update consentStatus with unsavedConsentStatus
     * @param action 
     * @param updatedBy 
     * @param isUpdateAll 
     */
    saveConsents(action: string, updatedBy: string, isUpdateAll?: boolean): void {
        const updatedConsents: Consent.Consent[] = isUpdateAll
            ? DataService.consents
            : ConsentsStatusService.getConsentsThatHaveChanged(DataService.consents);
        const updatedCategories: Consent.Category[] = isUpdateAll
            ? DataService.categories
            : ConsentsStatusService.getCategoriesThatHaveChanged(DataService.categories);
        const updatedAt: number = new Date().getTime();

        updatedConsents.forEach(consent => {
            SaveConsentsService.updateConsentParams(consent, action, updatedBy, updatedAt);
            this.utilityService.setTypeOfScriptTagForConsent(consent);
        });

        DataService.storeGlobally('consents', DataService.consents);
        DataService.updateSettingsStorage(DataService.settings.settingsId);
        DataExchangeService.updatePrivacyProxy(updatedConsents);

        if (action !== 'onInitialPageLoad') {
            this.pushConsentsToDataLayer(updatedConsents);
            this.pushCategoriesToDataLayer(updatedCategories);
            this.utilityService.crossDomainConsentSharingService.updateIframeStorageToLatest(updatedConsents);

            if (updatedBy === 'explicit') {
                if (this.utilityService.useIabTemplate()) {
                    this.updateConsentString();
                } else if (this.utilityService.settingsService.settings.iabConsentIsActive) {
                    this.setNonIabConsentString();
                }
            }
        } else if (this.utilityService.settingsService.settings.iabConsentIsActive) {
            this.writeConsentString(null);
        }

        this.customFunctionOnConsentSave();
        this.saveToServer(updatedConsents);
    }

    private getUpdatedVendorIds(): number[] {
        const iabIds: any = DataService.consents
            .filter(consent =>
                consent.consentStatus &&
                DataService.consentTemplates[consent.templateId][consent.version].iabId &&
                DataService.consentTemplates[consent.templateId][consent.version].iabId !== 0)
            .map(consent => Number(DataService.consentTemplates[consent.templateId][consent.version].iabId));

        return this.getCurrentVendorIds().filter(vendorId => iabIds.includes(vendorId));
    }

    public getCurrentVendorIds(): number[] {
        return this.utilityService.cmpIABService.vendors.map(vendor => vendor.id);
    }

    /**
     * update consent params and history
     * @param consent
     * @param action
     * @param updatedBy
     * @param updatedAt
     */
    static updateConsentParams(consent, action, updatedBy, updatedAt): void {
        consent.action = action;
        consent.updatedBy = updatedBy;
        consent.consentStatus = consent.unsavedConsentStatus;
        const history: Consent.History = {
            action: action,
            updatedBy: updatedBy,
            appVersion: pjson.version,
            settingsVersion: DataService.settings.version,
            consentTemplateVersion: consent.version,
            templateId: consent.templateId,
            consentStatus: consent.unsavedConsentStatus,
            language: DataService.settings.language,
            updatedAt: updatedAt
        };
        consent.history.unshift(history);
    }

    /**
     * Pushes an event for each individual consent and then one for all consents given status
     * @param updatedConsents 
     */
    public pushConsentsToDataLayer(updatedConsents: Consent.Consent[]): void {
        const dataExchangeService = new DataExchangeService();
        dataExchangeService.dataExchangeArray = this.utilityService.settingsService.settings.dataExchangeOnPage;
        updatedConsents.forEach(consent => {
            dataExchangeService.dispatchEventForConsent(consent, DataService.consentTemplates);
        });
        dataExchangeService.dispatchConsentChangeFinishedEvent(updatedConsents, DataService.consentTemplates);
        dataExchangeService.dispatchEvent('all_consents_given_status', {
            event: 'all_consents_given_status',
            status: ConsentsStatusService.selectAllStatus.isIntermediateState
                ? undefined
                : ConsentsStatusService.selectAllStatus.status
        });
    }

    /**
     * Pushes changes to the categories to the datalayer.
     * @param updatedCategories 
     */
    pushCategoriesToDataLayer(updatedCategories: Consent.Category[]): void {
        if (!updatedCategories) {
            return;
        }
        const dataExchangeService = new DataExchangeService();
        dataExchangeService.dataExchangeArray = this.utilityService.settingsService.settings.dataExchangeOnPage;
        updatedCategories.forEach(category => {
            dataExchangeService.dispatchCategoryChangedEventByCategory(
                category
            );
        });
    }

    customFunctionOnConsentSave(): void {
        this.utilityService.customFunctionsService.storeConsentStatusToLocalStorage();
        this.utilityService.customFunctionsService.storeEventToLocalStorage();
    }

    /**
     * Sends the given consent status to the Server
     * @param updatedConsents 
     */
    saveToServer(updatedConsents) {
        this.utilityService.developmentLog('saveConsentsToServer', updatedConsents);
        const paramsToSave: any = {
            operationName: 'saveConsents',
            query:
                'mutation saveConsents($consents: [NewCreateConsentInput]) {saveConsents(consents: $consents) { data { consentId deviceAuditId consentRowKey}}}',
            variables: {
                consents: updatedConsents.map(consent => {
                    return {
                        controllerId: consent.controllerId,
                        processorId: consent.processorId,
                        settingsId: DataService.settings.settingsId,
                        settingsVersion: DataService.settings.version,
                        language: DataService.settings.language ? DataService.settings.language : LanguageCode.default,
                        consentStatus: consent.consentStatus ? '1' : '0',
                        consentTemplateId: consent.templateId,
                        consentTemplateVersion: consent.version,
                        updatedBy: consent.updatedBy,
                        action: consent.action,
                        appVersion: pjson.version
                    };
                })
            }
        };
        return this.utilityService.apiService
            .saveConsents(paramsToSave)
            .then((response: any) => {
                if (response && response.data && response.data.createConsents) {
                    this.utilityService.developmentLog('saveConsentsToServer response', response.data);
                }
                return;
            })
            .catch((error: any) => {
                console.error('saveConsentsToServer: ', error);
            });
    }

    private changeNonIabVendorsStatus(status: boolean): void {
        DataService.consents
            .filter(consent =>
                !DataService.consentTemplates[consent.templateId][consent.version].iabId
                || this.utilityService.cmpIABService.NON_IAB_IDS.includes(consent.templateId))
            .forEach(consent => {
                ConsentsStatusService.setUnsavedConsentStatus(consent, status);
            });
    }

    private changePurposeStatus(purpose: IAB.Purpose, status: boolean): void {
        if (purpose.id !== this.utilityService.cmpIABService.DEFAULT_NON_IAB_PURPOSE.id) {
            const vendors = this.utilityService.cmpIABService.vendors.filter(vendor => vendor.purposeIds.includes(purpose.id));
            vendors.forEach(vendor => {
                const areAllPurposesSameStatus = this.utilityService.cmpIABService.areAllPurposesSameStatus(vendor, status);
                if (areAllPurposesSameStatus) {
                    const consent = GetConsentsService.getConsentByIabId(vendor.id);
                    if (consent) {
                        ConsentsStatusService.setUnsavedConsentStatus(consent, status);
                    }
                }
            });
        } else {
            this.changeNonIabVendorsStatus(status);
        }
    }

    updateConsentsFromPurposes(): void {
        this.utilityService.cmpIABService.purposes.forEach(purpose => {
            this.changePurposeStatus(purpose, this.utilityService.cmpIABService.purposeStatus[purpose.id]);
        });

        const action: string = 'onPurposeChange';
        const updatedBy: string = 'explicit';
        this.saveConsents(action, updatedBy);
    }

    updateConsentString(): void {
        const purposeStatus = this.utilityService.cmpIABService.purposeStatus;
        const vendorIds: number[] = this.getUpdatedVendorIds();
        const purposeIds: number[] = this.utilityService.cmpIABService.purposes
            .filter(purpose => purposeStatus[purpose.id])
            .map(purpose => purpose.id);

        const consentString = this.utilityService.cmpIABService.createConsentsStringAsBase64EncodedString(
            DataService.settings.language,
            vendorIds,
            purposeIds
        );
        this.writeConsentString(consentString);
    }

    writeConsentString(consentString: string): void {
        const consentSaved = new ConsentString(consentString);
        console.log('Consent String Saved', consentSaved);
        this.utilityService.developmentLog('Consent String Response', consentString);
        this.utilityService.cmpIABService.writeToEuConsentsCookie(consentString);
    }

    setNonIabConsentString() {
        const vendorsAllowedArray = DataService.consents
            .filter(e => {
                return (
                    e.consentStatus &&
                    DataService.consentTemplates[e.templateId][e.version].iabId &&
                    DataService.consentTemplates[e.templateId][e.version].iabId !== 0
                );
            })
            .map(e => DataService.consentTemplates[e.templateId][e.version].iabId);
        const consentString = this.utilityService.cmpIABService.createConsentsStringAsBase64EncodedString(
            DataService.settings.language,
            vendorsAllowedArray,
            [1, 2, 3, 4, 5]
        );
        this.utilityService.cmpIABService.writeToEuConsentsCookie(consentString);
    }
}
